import React, { useState } from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ConfirmDialog, LoadingState, StatusChip } from '../ui';
import type { StatusTone } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import {
  barcodeOverrideApi,
  storeActionSentence,
  type AffectedListing,
  type BarcodeOverrideResult,
  type BarcodeStoreAction,
  type BarcodeUseRow,
  type CatalogBarcodes,
} from './barcodeOverrideApi';

const BLOCK_COPY: Record<string, string> = {
  no_staged_barcode: 'This staged row has no barcode to write.',
  invalid_barcode: 'The staged barcode fails its GS1 check, so it cannot be written. Correct it in staging first.',
  conflicting_barcodes: 'The staged row states two different codes for one field. Correct it in staging first.',
  no_change: 'The catalog already carries these barcodes.',
  new_barcode_on_other_product: 'The new barcode is already on another active catalog product. Fix that product first.',
};

const STATE_LABEL: Record<AffectedListing['state'], { label: string; tone: StatusTone }> = {
  carries_old: { label: 'Carries the old barcode', tone: 'caution' },
  unconfirmed: { label: 'Could not confirm', tone: 'neutral' },
  already_new: { label: 'Already correct', tone: 'positive' },
  different: { label: 'Carries another code', tone: 'neutral' },
  store_own: { label: "Store's own barcode", tone: 'info' },
};

const FIELDS: Array<keyof CatalogBarcodes> = ['upc', 'ean', 'gtin'];

function BarcodeTable({ before, after, cleared }: { before: CatalogBarcodes; after: CatalogBarcodes; cleared: string[] }) {
  return (
    <Table size="small" aria-label="Catalog barcode change">
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Catalog now</TableCell>
          <TableCell>After the override</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {FIELDS.filter((field) => before[field] || after[field]).map((field) => (
          <TableRow key={field}>
            <TableCell>{field.toUpperCase()}</TableCell>
            <TableCell><code>{before[field] || 'none'}</code></TableCell>
            <TableCell>
              {cleared.includes(field) ? 'cleared' : <code>{after[field] || 'none'}</code>}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function UsesList({ title, rows }: { title: string; rows: BarcodeUseRow[] }) {
  if (rows.length === 0) return null;
  return (
    <Box>
      <Typography variant="subtitle2">{title}</Typography>
      {rows.map((row) => (
        <Typography key={`${row.collection}/${row.id}`} variant="body2" color="text.secondary">
          {row.collection === 'canonicalProducts' ? 'Catalog' : 'Staged'}: {row.name || row.id} ({row.sku || 'no SKU'}), {row.field.toUpperCase()} <code>{row.value}</code>, {row.status || 'no status'}
        </Typography>
      ))}
    </Box>
  );
}

export const BarcodeOverrideDialog: React.FC<{
  open: boolean;
  stagedId: string;
  onClose: () => void;
  onApplied: (result: BarcodeOverrideResult) => void;
}> = ({ open, stagedId, onClose, onApplied }) => {
  const { showToast } = useToast();
  // No default: the admin chooses each time.
  const [storeAction, setStoreAction] = useState<BarcodeStoreAction | null>(null);
  const previewQuery = useQuery({
    queryKey: ['barcode-override-preview', stagedId],
    queryFn: () => barcodeOverrideApi.preview(stagedId),
    enabled: open,
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
  const preview = previewQuery.data;
  const affected = (preview?.counts.carryingOld || 0) + (preview?.counts.unconfirmed || 0);
  const apply = useMutation({
    mutationFn: () => barcodeOverrideApi.apply(stagedId, preview!.before, preview!.after, affected > 0 ? storeAction : null),
    onSuccess: (result) => {
      showToast(`Catalog barcode overridden. ${storeActionSentence(result.store)}`, 'success');
      setStoreAction(null);
      onApplied(result);
    },
    onError: (error: Error) => showToast(error.message, 'error'),
  });
  const counted = (preview?.listings || []).filter((row) => row.state === 'carries_old' || row.state === 'unconfirmed');
  const notCounted = (preview?.listings || []).filter((row) => row.state !== 'carries_old' && row.state !== 'unconfirmed');

  const body = previewQuery.isLoading ? (
    <LoadingState message="Checking the catalog and every store listing" />
  ) : previewQuery.isError ? (
    <Alert severity="error">{(previewQuery.error as Error).message}</Alert>
  ) : preview ? (
    <Stack spacing={2}>
      {preview.block ? <Alert severity="error">{BLOCK_COPY[preview.block] || preview.block}</Alert> : null}
      {preview.rejects.map((reject) => (
        <Alert key={reject.value} severity="error">
          <code>{reject.value}</code> was refused: {reject.reason.replace(/_/g, ' ')}.
        </Alert>
      ))}
      <BarcodeTable before={preview.before} after={preview.after} cleared={preview.clearedFields} />
      <Typography variant="caption" color="text.secondary">
        These fields are then locked: a scrape or a promotion keeps them until an admin changes them.
      </Typography>
      <UsesList title="Other rows on the new barcode" rows={preview.uses.filter((row) => row.uses === 'new')} />
      <UsesList title="Other rows still using the old barcode" rows={preview.uses.filter((row) => row.uses === 'old')} />

      {preview.listings.length > 0 ? (
        <Box>
          <Typography variant="subtitle2">
            {affected} store listing{affected === 1 ? '' : 's'} in {preview.counts.stores} store{preview.counts.stores === 1 ? '' : 's'} may still carry the old barcode
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
            {preview.counts.carryingOld} confirmed, {preview.counts.unconfirmed} could not be confirmed. Motzu can write {preview.counts.writable}; {preview.counts.cannotWrite} must be corrected by the store.
            Prices keep syncing on all of them, and nothing is pushed until you or the store chooses it.
          </Typography>
          <Table size="small" aria-label="Affected store listings">
            <TableHead>
              <TableRow>
                <TableCell>Store</TableCell>
                <TableCell>Connection</TableCell>
                <TableCell>Listing</TableCell>
                <TableCell>What happens</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...counted, ...notCounted].map((row) => (
                <TableRow key={row.mappingDocId}>
                  <TableCell>{row.organizationName || row.organizationId}</TableCell>
                  <TableCell>{row.integrationName || row.platform}</TableCell>
                  <TableCell>
                    <StatusChip tone={STATE_LABEL[row.state].tone} label={STATE_LABEL[row.state].label} />
                    {row.listingBarcode ? <Typography variant="caption" component="div"><code>{row.listingBarcode}</code></Typography> : null}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {row.state === 'carries_old' || row.state === 'unconfirmed' ? row.fixMessage : 'Not touched.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      {!preview.block && affected > 0 ? (
        <FormControl>
          <FormLabel id="barcode-store-action">What should happen for these stores?</FormLabel>
          <RadioGroup
            aria-labelledby="barcode-store-action"
            value={storeAction ?? ''}
            onChange={(event) => setStoreAction(event.target.value as BarcodeStoreAction)}
          >
            <FormControlLabel
              value="notify"
              control={<Radio size="small" />}
              label="Notify the stores. Each store sees the change and can apply the fix in one click where its connection allows it."
            />
            <FormControlLabel
              value="push"
              control={<Radio size="small" />}
              label={`Push the fix now to the ${preview.counts.writable} listing${preview.counts.writable === 1 ? '' : 's'} Motzu can write, and tell every store what happened. This runs the store's normal sync for this product.`}
            />
          </RadioGroup>
        </FormControl>
      ) : null}
    </Stack>
  ) : null;

  return (
    <ConfirmDialog
      open={open}
      title="Override catalog barcode"
      message={preview ? <>Force the staged barcode onto <strong>{preview.productName}</strong>.</> : 'Force the staged barcode onto the catalog product.'}
      preview={body}
      maxWidth="md"
      confirmLabel="Override catalog barcode"
      confirmColor="warning"
      loading={apply.isPending}
      confirmDisabled={!preview || Boolean(preview.block) || (affected > 0 && !storeAction)}
      onConfirm={() => apply.mutate()}
      onClose={() => {
        setStoreAction(null);
        onClose();
      }}
    />
  );
};
